/**
 * @file forms-responses-export.ts
 * @module presentation/utils/exportUtils/forms-responses-export
 *
 * @description
 * Form responses Excel export utility.
 *
 * Generates a matrix-style XLSX where each row represents a question from
 * the template and each column represents a single submission. Header rows
 * include the submission timestamp and the slug of the originating instance,
 * so the resulting spreadsheet doubles as a human-readable audit of every
 * answer collected for a given form template.
 *
 * @remarks
 * **Pending backend optimization (N+1 fetch waterfall):**
 * The current flow performs three sequential rounds of HTTP requests:
 *   1. List instances for the template.
 *   2. For each instance, fetch submissions.
 *   3. For each submission, fetch answers.
 *
 * For forms with many instances/submissions this can fire dozens of
 * requests in series and noticeably delay the export. The intended fix is
 * a single aggregated endpoint:
 *
 *   `GET /formsTemplante/{id}/responses`
 *
 * returning `{ template, questions, instances, submissions, answers }` in a
 * single response. Until that endpoint exists, the per-id repositories
 * below are used and the parallelization happens client-side via
 * {@link Promise.all}.
 */

import * as XLSX from "xlsx";
import { FormTemplate } from "domain/entities/form-template";
import { FormQuestion } from "domain/entities/form-question";
import { FormInstance } from "domain/entities/form-instance";
import { Answer } from "domain/entities/answer";
import { Submission } from "domain/entities/submission";
import formsInstanceRepository from "infra/repositories/formsinstance-repository";
import submissionRepository from "infra/repositories/submission-repository";
import answerRepository from "infra/repositories/answer-repository";
import questionRepository from "infra/repositories/question-repository";
import { FormSubmissionInputFilter } from "domain/models/nimberforms-input-filters";
import { AxiosError } from "axios";
import { Logger } from "infra/logger/logger";

type matrices = {
    answers?: (string | number)[][];
    personal?: (string | number)[][];
    both?: (string | number)[][];
}

/**
 * Formats a date value using the Portuguese (Portugal) locale in the
 * `dd/MM/yyyy, HH:mm` style used across the spreadsheet metadata rows.
 *
 * @param value - A `Date` instance, an ISO/parseable date string, or `undefined`.
 * @returns The formatted date, or an empty string when `value` is missing
 *          or cannot be parsed into a valid date.
 *
 * @example
 * formatDate("2026-05-14T10:30:00Z"); // "14/05/2026, 11:30" (TZ dependent)
 * formatDate(undefined);              // ""
 * formatDate("not-a-date");           // ""
 */
const formatDate = (value: string | Date | undefined): string => {
    if (!value) return "";
    const d = value instanceof Date ? value : new Date(value);
    if (isNaN(d.getTime())) return "";
    return d.toLocaleString("pt-PT", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
};

/**
 * Converts an arbitrary string into a filename-safe slug.
 *
 * The transformation pipeline is:
 *   1. Lowercase the input.
 *   2. Normalize to NFD and strip diacritics (so `ç`, `ã`, `é`… become
 *      `c`, `a`, `e`).
 *   3. Replace any run of non-alphanumeric characters with a single `-`.
 *   4. Trim leading/trailing dashes.
 *   5. Cap the slug length at 60 characters.
 *
 * Empty results fall back to the literal `"formulario"` so the produced
 * filename is never empty or starts with an underscore separator only.
 *
 * @param text - The raw text to slugify (typically the form template title).
 * @returns A lowercase, dash-separated, ASCII-only slug, max 60 chars.
 *
 * @example
 * slugify("Avaliação Final - 2026");        // "avaliacao-final-2026"
 * slugify("   ");                            // "formulario"
 */
const slugify = (text: string): string =>
    text
        .toLowerCase()
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .slice(0, 60) || "formulario";


export const exportFormResponsesToExcel = async (
    filter: FormSubmissionInputFilter, 
    instanceTitle: string
): Promise<null | Submission[]> => {
    
    const submissions = await getSubmissions(filter);
    if(!submissions)
        return null;

    const matrice = buildMatrix(submissions, filter.includeAnswers, filter.isAnonymous, instanceTitle);

    createBook(matrice, instanceTitle);

    return submissions;
};

function createBook(matrice: matrices, instanceTitle: string){
    const workBook = XLSX.utils.book_new();
    if(matrice.answers){
        const answerSheet = XLSX.utils.aoa_to_sheet(matrice.answers);
        XLSX.utils.book_append_sheet(workBook, answerSheet, "Respostas");
    }

    if(matrice.personal){
        const personalSheet = XLSX.utils.aoa_to_sheet(matrice.personal);
        XLSX.utils.book_append_sheet(workBook, personalSheet, "Dados Pessoais");
    }

    if(matrice.both){
        const bothSheet = XLSX.utils.aoa_to_sheet(matrice.both);
        XLSX.utils.book_append_sheet(workBook, bothSheet, "Respostas e Dados Pessoais");
    }

    saveFile(instanceTitle, workBook, getExportType(matrice));
}

function getExportType(matrice: matrices): string {
    if(matrice.answers && matrice.personal) return "respostas-submissoes";
    if(matrice.answers) return "respostas";
    if(matrice.personal) return "submissoes";
    return "exportacao";
}

function saveFile(instanceName: string, workBook: XLSX.WorkBook, exportType: string){
    const filename = `${slugify(instanceName)}-${exportType}_${new Date().toISOString().slice(0, 10)}.xlsx`
    XLSX.writeFile(workBook, filename);
}

async function getSubmissions(filter: FormSubmissionInputFilter): Promise<Submission[] | undefined> {
    const logger = Logger.getInstance();
    try{
        const submissions = await submissionRepository.getByFilter(filter);
        return submissions.data;
    }catch(caught){
        console.log(caught); //TODO: give a more detailed error.
    }
}

function buildMatrix(
    submissions: Submission[], 
    includeAnswers: boolean = false, 
    isAnonymous: boolean = false,
    instanceTitle: string
): matrices
{
    if(isAnonymous && !includeAnswers)
        return {};
    const matrice: matrices = {};
    console.log("Building matrix with params: ", {includeAnswers, isAnonymous})
    
    if(!isAnonymous && !includeAnswers)
        matrice.personal = buildPersonalMatrix(submissions, instanceTitle);

    if(includeAnswers && isAnonymous)
        matrice.answers = buildAnswersMatrix(submissions, instanceTitle);
    
    if(includeAnswers && !isAnonymous){
        matrice.both = buildBothMatrix(submissions, instanceTitle);
    }
    return matrice;
}

function buildPersonalMatrix(submissions: Submission[], instanceTitle: string): (string|number)[][]{
    const header = ["Nome", "Email", "Número de Télemovel", "Aceitou Rgpd", "Nome da Instância", "Data de Submissão", "ID da Submissão"];
    const matrix: (string|number)[][] = [];
    matrix.push(header);

    submissions.map(submission => {
        const acceptedRgpd = submission.acceptedRgpd ? "Sim" : "Não";
        //Since this method only gets called when the intance ins't anonymous the values should never be undefined
        const nonExistant = "-";
        const row = [
            submission.name ?? nonExistant, 
            submission.email ?? nonExistant, 
            submission.numberPhone ?? nonExistant, 
            acceptedRgpd, 
            instanceTitle,
            formatDate(submission.createdAt ?? new Date()),
            submission.id ?? nonExistant
        ];
        matrix.push(row);
    })

    return matrix;
}

function buildAnswersMatrix(submissions: Submission[], instanceTitle: string): (string|number)[][]{
    const header = ["Pergunta", "Tipo", "Resposta", "Nome da Instância", "Data de Submissão", "ID da Submissão"];
    const matrix: (string|number)[][] = [];
    matrix.push(header);
    submissions.map(submission => {
        const answerCount = submission.answers?.length ?? 0;
        if(answerCount > 0 && submission.answers){
            submission.answers.map(answer => {
                const questionName = answer.question?.label ?? "-";
                const questionType = answer.question?.type ?? "-";
                const row = [
                    questionName, 
                    questionType, 
                    answer.value, 
                    instanceTitle, 
                    formatDate(submission.createdAt ?? new Date()), 
                    submission.id ?? "-"
                ];
                matrix.push(row);
            })
        }
    })
    return matrix;
}

function buildBothMatrix(submissions: Submission[], instanceTitle: string): (string|number)[][]{
    console.log("Both")
    const header = [
        "Pergunta",
         "Tipo", 
         "Resposta", 
         "Nome", 
         "Email", 
         "Número de Télemovel", 
         "Aceitou Rgpd",
         "Nome da Instância", 
         "Data de Submissão", 
         "ID da Submissão"
        ];

    const matrix: (string|number)[][] = [];
    matrix.push(header);

    const nonExistant = "-";
    
    submissions.map(submission => {

        const acceptedRgpd = submission.acceptedRgpd ? "Sim" : "Não";
        const answerCount = submission.answers?.length ?? 0;
        
        if(answerCount > 0 && submission.answers){
            submission.answers.map(answer => {
            
                const questionName = answer.question?.label ?? "-";
                const questionType = answer.question?.type ?? "-";
                const row = [
                    questionName, 
                    questionType, 
                    answer.value,
                    submission.name ?? nonExistant, 
                    submission.email ?? nonExistant, 
                    submission.numberPhone ?? nonExistant, 
                    acceptedRgpd, 
                    instanceTitle, 
                    formatDate(submission.createdAt ?? new Date()), 
                    submission.id ?? "-"
                ];

                matrix.push(row);
            
            })
        }
    })
    return matrix;
}
